import { createStore } from 'vuex'
import { getProductList } from '@/api/product'
import { processProductImageUrlSync } from '@/utils/imageUtils'
import { isWechatBrowser } from '@/utils/wechatEnvironment'
import { refreshGoogleToken, googleLogin } from '@/api/google'

// Import image assets directly
import shopImage from '@/assets/shop.jpg'
import shop1Image from '@/assets/shop1.jpg'

export default createStore({
  state: {
    products: [],
    // 用户认证状态
    user: null,
    token: null,
    isLoggedIn: false,
    // 微信登录相关状态
    wechatLoginState: {
      isProcessing: false,
      error: null
    },
    // Google登录相关状态
    googleLoginState: {
      isProcessing: false,
      error: null,
      isInitialized: false
    }
  },
  mutations: {
    setProducts(state, products) {
      state.products = products
    },
    appendProducts(state, products) {
      state.products = [...state.products, ...products]
    },
    // 用户认证相关 mutations
    setUser(state, userData) {
      state.user = userData
      state.isLoggedIn = true
    },
    setToken(state, token) {
      state.token = token
    },
    clearUser(state) {
      state.user = null
      state.token = null
      state.isLoggedIn = false
    },
    initUserFromStorage(state) {
      try {
        if (typeof localStorage === 'undefined') {
          return
        }
        
        // 检查Google登录token
        const googleToken = localStorage.getItem('google_token')
        const googleUserInfo = localStorage.getItem('user_info')
        
        // 检查传统token（兼容性）
        const token = localStorage.getItem('token')
        const userInfo = localStorage.getItem('userInfo')
        
        // 优先使用Google登录信息
        if (googleToken && typeof googleToken === 'string') {
          state.token = googleToken
          state.isLoggedIn = true
        } else if (token && typeof token === 'string') {
          state.token = token
          state.isLoggedIn = true
        }
        
        // 优先使用Google用户信息
        if (googleUserInfo && typeof googleUserInfo === 'string') {
          const parsedUser = JSON.parse(googleUserInfo)
          if (parsedUser && typeof parsedUser === 'object') {
            state.user = parsedUser
          }
        } else if (userInfo && typeof userInfo === 'string') {
          const parsedUser = JSON.parse(userInfo)
          if (parsedUser && typeof parsedUser === 'object') {
            state.user = parsedUser
          }
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error)
        // Clear potentially corrupted data
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('google_token')
          localStorage.removeItem('user_info')
          localStorage.removeItem('google_refresh_token')
        }
        state.token = null
        state.user = null
        state.isLoggedIn = false
      }
    },
    // 微信登录状态管理
    setWechatLoginProcessing(state, isProcessing) {
      state.wechatLoginState.isProcessing = isProcessing
    },
    setWechatLoginError(state, error) {
      state.wechatLoginState.error = error
    },
    clearWechatLoginError(state) {
      state.wechatLoginState.error = null
    },
    // Google登录状态管理
    setGoogleLoginProcessing(state, isProcessing) {
      state.googleLoginState.isProcessing = isProcessing
    },
    setGoogleLoginError(state, error) {
      state.googleLoginState.error = error
    },
    clearGoogleLoginError(state) {
      state.googleLoginState.error = null
    },
    setGoogleLoginInitialized(state, isInitialized) {
      state.googleLoginState.isInitialized = isInitialized
    }
  },
  actions: {
    // 设置用户信息
    setUser({ commit }, userData) {
      commit('setUser', userData)
    },
    
    // 设置token
    setToken({ commit }, token) {
      commit('setToken', token)
    },
    
    // 用户登录
    login({ commit }, { user, token }) {
      if (!user || !token) {
        console.error('Login failed: user or token is null or undefined')
        return
      }
      
      try {
        commit('setUser', user)
        commit('setToken', token)
        // 保存到本地存储，统一使用Google登录的存储键
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('google_token', token)
          localStorage.setItem('user_info', JSON.stringify(user))
          // 清除旧的存储键以避免冲突
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
        }
      } catch (error) {
        console.error('Login error:', error)
      }
    },
    
    // 用户退出
    logout({ commit }) {
      try {
        commit('clearUser')
        // 清除本地存储（包括Google登录数据）
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('google_token')
          localStorage.removeItem('user_info')
          localStorage.removeItem('google_refresh_token')
        }
      } catch (error) {
        console.error('Logout error:', error)
      }
    },

    // Token登录
    async tokenLogin({ dispatch }, { token, username, email }) {
      try {
        if (!token || typeof token !== 'string' || token.trim() === '') {
          throw new Error('Invalid token provided')
        }

        // 模拟token验证（在实际应用中，这里应该调用API验证token）
        const isValidToken = await new Promise((resolve) => {
          setTimeout(() => {
            // 更宽松的token格式验证 - 允许大部分可打印字符，排除空格
            const trimmedToken = token.trim()
            const hasValidLength = trimmedToken.length >= 6 // 降低最小长度要求
            const hasNoSpaces = !/\s/.test(trimmedToken) // 不包含空格
            const isNotEmpty = trimmedToken.length > 0
            
            resolve(isNotEmpty && hasValidLength && hasNoSpaces)
          }, 500)
        })

        if (!isValidToken) {
          throw new Error('Invalid token format')
        }

        // 构造用户信息
        const userData = {
          id: `token_user_${Date.now()}`,
          username: username || 'Token User',
          email: email || 'token@example.com',
          avatar: '',
          loginType: 'token',
          loginTime: new Date().toISOString(),
          tokenInfo: {
            token: token,
            issuedAt: new Date().toISOString(),
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24小时后过期
          }
        }

        // 调用通用登录方法
        await dispatch('login', {
          user: userData,
          token: token
        })

        return {
          success: true,
          user: userData,
          message: 'Token login successful'
        }

      } catch (error) {
        console.error('Token login error:', error)
        throw new Error(error.message || 'Token login failed')
      }
    },
    
    // 微信登录处理
    async wechatLogin({ commit }) {
      commit('setWechatLoginProcessing', true)
      commit('clearWechatLoginError')
      
      try {
        // 这里应该调用实际的API
        // const response = await api.wechatLogin({ code, state })
        
        // 模拟API调用
        const response = await new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              success: true,
              data: {
                user: {
                  id: '123456',
                  nickname: '微信用户',
                  avatar: 'https://via.placeholder.com/100x100',
                  openid: 'mock_openid_' + Date.now()
                },
                token: 'mock_token_' + Date.now()
              }
            })
          }, 1000)
        })
        
        if (response.success) {
          commit('setUser', response.data.user)
          commit('setToken', response.data.token)
          
          // 保存到本地存储
          localStorage.setItem('token', response.data.token)
          localStorage.setItem('userInfo', JSON.stringify(response.data.user))
          
          return response.data
        } else {
          throw new Error(response.message || '登录失败')
        }
      } catch (error) {
        commit('setWechatLoginError', error.message)
        throw error
      } finally {
        commit('setWechatLoginProcessing', false)
      }
    },
    
    // Google登录处理
    async googleLogin({ commit }, { idToken }) {
      commit('setGoogleLoginProcessing', true)
      commit('clearGoogleLoginError')
      
      try {
        // 调用后端API进行Google登录
        const response = await googleLogin(idToken)
        
        if (response.code === 200) {
          const { token, user_info } = response.data
          
          commit('setUser', user_info)
          commit('setToken', token)
          
          // 保存到本地存储
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('google_token', token)
            localStorage.setItem('user_info', JSON.stringify(user_info))
          }
          
          return response.data
        } else {
          throw new Error(response.msg || 'Google登录失败')
        }
      } catch (error) {
        commit('setGoogleLoginError', error.message)
        throw error
      } finally {
        commit('setGoogleLoginProcessing', false)
      }
    },
    
    // 初始化用户状态
    initUser({ commit }) {
      try {
        if (typeof localStorage === 'undefined') {
          console.warn('localStorage is not available')
          return
        }
        commit('initUserFromStorage')
      } catch (error) {
        console.error('Error initializing user:', error)
      }
    },

    
    // 刷新Google Token
    async refreshGoogleToken({ commit }) {
      try {
        const refreshToken = localStorage.getItem('google_refresh_token')
        if (!refreshToken) return null
        
        const response = await refreshGoogleToken(refreshToken)
        if (response.code === 200) {
          const { token, refresh_token } = response.data
          
          // 更新存储的token
          commit('setToken', token)
          localStorage.setItem('google_token', token)
          
          if (refresh_token) {
            localStorage.setItem('google_refresh_token', refresh_token)
          }
          
          return token
        }
      } catch (error) {
        console.error('Token refresh failed:', error)
        // 如果刷新失败，清除用户状态
        commit('clearUser')
        localStorage.removeItem('google_token')
        localStorage.removeItem('google_refresh_token')
        localStorage.removeItem('user_info')
      }
      return null
    },

    async fetchProducts({ commit }, { typeIds = null, page = 1, limit = 10, append = false } = {}) {
      try {
        let apiResponse
        if (typeIds && typeIds.length > 0) {
          // 根据分类ID获取商品
          apiResponse = await getProductList(typeIds.toString(), page, limit)
        } else {
          // 获取所有分类的商品（全部）
          apiResponse = await getProductList('1,2,3,4,5,6,7,8,9,10,11', page, limit)
        }
        
        // 处理API响应数据格式
        const apiData = apiResponse.data || apiResponse
        const apiProducts = Array.isArray(apiData.list) ? apiData.list : (Array.isArray(apiData) ? apiData : [])
        
        // 将API数据转换为前端需要的格式
        const products = apiProducts.map((item, index) => {
          // 处理图片URL，确保使用HTTP协议
          let imageUrl = item.image || item.images
          
          // 如果API返回的图片URL存在，确保使用HTTP协议
          if (imageUrl) {
            imageUrl = processProductImageUrlSync(imageUrl)
          } else {
            // 如果没有图片，使用本地备用图片
            imageUrl = index % 2 === 0 ? shopImage : shop1Image
          }
          
          /**
           * 根据产品类型获取产品信息
           * @param {number} productType - 产品类型：1=现货, 2=定制, 3=预售
           * @param {number} customDuration - 定制时长（小时）
           * @returns {Object} 包含标签和发货时间的对象
           */
          const getProductTypeInfo = (productType, customDuration) => {
            /**
             * 格式化发货时间：≤72小时显示小时，>72小时显示天
             * @param {number} duration - 时长（小时）
             * @returns {string} 格式化后的发货时间字符串
             */
            const formatDeliveryTime = (duration) => {
              if (duration <= 72) {
                return `Ships within ${duration} hours`;
              } else {
                const days = Math.ceil(duration / 24);
                return `Ships within ${days} days`;
              }
            }
            
            switch (productType) {
              case 1:
                return { tag: 'In Stock', deliveryTime: 'Ships within 24 hours' };
              case 2:
                return { tag: 'Custom', deliveryTime: formatDeliveryTime(customDuration || 720) };
              case 3:
                return { tag: 'Pre-sale', deliveryTime: formatDeliveryTime(customDuration || 168) };
              default:
                return { tag: 'In Stock', deliveryTime: 'Ships within 24 hours' };
            }
          }
          
          const typeInfo = getProductTypeInfo(item.product_type, item.custom_duration);
          
          return {
            id: item.id,
            name: `【${typeInfo.tag}】${item.name}`, // 在标题前添加类型标签
            price: item.price, // 直接使用API返回的价格
            image: imageUrl,
            deliveryTime: typeInfo.deliveryTime,
            productType: item.product_type, // 原始产品类型
            productTypeTag: typeInfo.tag, // 用于显示的标签
            description: item.description,
            stock: item.stock,
            status: item.status,
            // 保持使用 type_id 以匹配 API 响应
            type_id: item.type_id,
            salesCount: item.sales_count || 0,
            viewCount: item.view_count || 0,
            customDuration: item.custom_duration || 0
          }
        })
        
        if (append) {
          commit('appendProducts', products)
        } else {
          commit('setProducts', products)
        }
        
        return {
          products,
          pagination: {
            page: apiData.page || page,
            limit: apiData.limit || limit,
            total: apiData.total || products.length,
            pages: apiData.pages || Math.ceil((apiData.total || products.length) / limit)
          }
        }
      } catch (error) {
        console.error('获取商品列表失败:', error)
        // 如果API调用失败，使用备用数据
        const fallbackProducts = [
          {
            id: 1,
            name: '【Custom】2023 MONGOLJINGOO Traditional Clothing',
            price: 6500.00,
            image: shopImage,
            deliveryTime: '30天',
            productType: 2,
            productTypeTag: '定制'
          },
          {
            id: 2,
            name: '【Custom】MONGOLJINGOO AB Style Traditional Clothing',
            price: 10000.00,
            image: shop1Image,
            deliveryTime: '30天',
            productType: 2,
            productTypeTag: '定制'
          }
        ]
        commit('setProducts', fallbackProducts)
        return {
          products: fallbackProducts,
          pagination: {
            page,
            limit,
            total: fallbackProducts.length,
            pages: Math.ceil(fallbackProducts.length / limit)
          }
        }
      }
    }
  },
  getters: {
    isWechat: () => {
      return isWechatBrowser()
    },
    // 用户相关 getters
    isLoggedIn: state => state.isLoggedIn,
    userInfo: state => state.user,
    userToken: state => state.token,
    userId: state => state.user?.id,
    // Google登录相关 getters
    isGoogleLoginProcessing: state => state.googleLoginState.isProcessing,
    googleLoginError: state => state.googleLoginState.error,
    isGoogleLoginInitialized: state => state.googleLoginState.isInitialized
  },
  modules: {
  }
})
